import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
}

const DRAG_DISMISS_PX = 120;

// Modal/sheet wrapper for "drill into one thing" surfaces. Desktop renders
// a centered modal (~600px wide) with a darkened backdrop. Mobile renders
// a bottom sheet that supports drag-down-to-dismiss in addition to the
// close button and backdrop tap.
export default function Modal({ open, onClose, children, ariaLabel }: Props) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStateRef = useRef<{ startY: number; pointerId: number } | null>(null);

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isMobile()) return;
    if (e.target instanceof HTMLElement) {
      // Ignore drags that begin inside scrollable content or interactive elements.
      const interactive = e.target.closest(
        "button, a, input, textarea, select, [data-modal-no-drag]",
      );
      if (interactive) return;
    }
    dragStateRef.current = { startY: e.clientY, pointerId: e.pointerId };
    sheetRef.current?.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const delta = e.clientY - drag.startY;
    setDragOffset(Math.max(0, delta));
  }, []);

  const onPointerEnd = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragStateRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      sheetRef.current?.releasePointerCapture(e.pointerId);
      const dismissed = dragOffset >= DRAG_DISMISS_PX;
      dragStateRef.current = null;
      setDragOffset(0);
      if (dismissed) onClose();
    },
    [dragOffset, onClose],
  );

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        className="modal-sheet"
        style={dragOffset > 0 ? { transform: `translateY(${dragOffset}px)` } : undefined}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        <div className="modal-sheet__handle" aria-hidden="true" />
        <button
          type="button"
          className="modal-sheet__close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
        <div className="modal-sheet__body">{children}</div>
      </div>
    </div>
  );
}

function isMobile(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 719px)").matches;
}
